import './style.modules.scss'

import {
  BrandIcon,
  BrandIconColor,
  BrandIconId,
  Link,
  LinkProps,
  LinkSize,
  LinkVariant,
  Spacer,
  SpacerSpacing
} from '@ampcore/adel-react'
import { BackgroundColor } from '@ampcore/ampcom-contracts'
import {
  AdvisorDataService,
  EmailPayloadTemplate,
  FaceProfileData
} from '@ampcore/client-side-services'
import Cookies from 'js-cookie'
import React, { useEffect, useState } from 'react'
import xss from 'xss'

import { useAmpUserStatus } from '../../hooks/useAmpUserStatus'
import { usePageHeading } from '../../hooks/usePageHeading'
import { usePageTitle } from '../../hooks/usePageTitle'
import { EmailModal } from '../../types/EmailModal.type'
import { UserStatus } from '../../types/UserStatus.enum'
import { EmailAdvisorModal } from '../EmailAdvisorModal/EmailAdvisorModal'
import { OneColumnBannerBackgroundColor } from './OneColumnBannerBackgroundColor.type'
import { OneColumnBannerVariant } from './OneColumnBannerVariant.type'

export type OneColumnBannerProps = {
  textAlignment?: 'left' | 'center' | 'right' | 'none'
  textColor?: 'white' | 'black' | 'none'
  buttonColor?: 'white' | 'blue' | 'none'
  backgroundColor?: OneColumnBannerBackgroundColor
  backgroundImage?: string
  heading?: string
  content?: string | string[]
  icon?: BrandIconId
  iconColor?: BrandIconColor
  fill?: boolean
  stroke?: boolean
  links?: LinkProps[] | LinkProps
  componentSpacing?: SpacerSpacing
  variant?: OneColumnBannerVariant
  testId?: string
  contentWidth?: '8-column' | '12-column'
}

export type OneColumnBannerEmailProps = {
  textAlignment?: 'left' | 'center' | 'right' | 'none'
  textColor?: 'white' | 'black' | 'none'
  buttonColor?: 'white' | 'blue' | 'none'
  client?: OneColumnBannerProps
  prospect?: OneColumnBannerProps
  email?: EmailModal
  buttonTextPhone?: string
  componentSpacing?: SpacerSpacing
  variant?: OneColumnBannerVariant
  testId?: string
  contentWidth?: '8-column' | '12-column'
}

export const OneColumnBanner = (
  props: OneColumnBannerProps | OneColumnBannerEmailProps
) => {
  const [activeProps, setActiveProps] = useState<OneColumnBannerProps>({})
  const [isModalOpen, setIsModalOpen] = useState(false)
  const faceProfileCookie = Cookies.get('FaceProfileCookie')
  const userStatus = useAmpUserStatus()
  const [advisorData, setAdvisorData] = useState<FaceProfileData | undefined>()
  const pageHeading = usePageHeading()
  const pageTitle = usePageTitle()
  const {
    textAlignment,
    textColor,
    buttonColor,
    componentSpacing,
    variant,
    contentWidth,
    testId
  } = props
  const { email, buttonTextPhone } = props as OneColumnBannerEmailProps
  useEffect(() => {
    if (
      props.variant === OneColumnBannerVariant.ONE_COLUMN_BANNER_EMAIL &&
      ('prospect' in props || 'client' in props)
    ) {
      setActiveProps(props.prospect as OneColumnBannerEmailProps)
      if (
        faceProfileCookie &&
        (userStatus == UserStatus.CLIENT_ACTIVE ||
          userStatus == UserStatus.CLIENT_EXPIRED)
      ) {
        const fetchData = async () => {
          const advisorDataService = new AdvisorDataService()
          const faceProfileResponse =
            await advisorDataService.getById(faceProfileCookie)
          if (
            faceProfileResponse?.data?.phone ||
            faceProfileResponse?.data?.email
          ) {
            setActiveProps(props.client as OneColumnBannerEmailProps)
          }
          setAdvisorData(faceProfileResponse.data ?? undefined)
        }
        fetchData()
      }
    } else {
      setActiveProps(props as OneColumnBannerProps)
    }
  }, [props, faceProfileCookie, userStatus])

  const {
    backgroundColor,
    backgroundImage,
    heading,
    content,
    icon,
    iconColor,
    fill,
    stroke,
    links
  } = activeProps

  const overrideRecipient = email?.overrideRecipient
  const emailSubject = email?.emailSubject
  const emailGreeting = email?.emailGreeting
  const disclosureText = email?.disclosureText
  const emailBody = email?.emailBody

  const hasFacet = backgroundColor?.includes('facet')
  let facetColor = ''
  const bannerLinks = links ? (Array.isArray(links) ? links : [links]) : []
  const bannerContent = content
    ? Array.isArray(content)
      ? content
      : [content]
    : []

  const handleOpenModal = () => {
    setIsModalOpen(true)
  }

  const closeModal = () => {
    setIsModalOpen(false)
  }

  const emailMessage = () => {
    if (!emailBody) {
      return ''
    }

    const placeholderLinkText = (pageHeading || pageTitle || '').trim()

    const isPlainText = !/<[a-z][\s\S]*>/i.test(emailBody)
    let message = emailBody
    if (isPlainText) {
      message = message?.replace(/(\r\n|\n|\r)/g, '<br/>')
    }
    return message?.replace(
      '%article%',
      `<a href="${window.location.href}" target="_blank" rel="noopener noreferrer">${placeholderLinkText}</a>`
    )
  }

  let bgClassName = ''
  let facet = ''
  switch (backgroundColor) {
    case BackgroundColor.MIDNIGHT_BLUE:
      bgClassName = 'u-bgColorMidnightBlue'
      break
    case BackgroundColor.PURPLE:
      bgClassName = 'u-bgColorPurple'
      break
    case BackgroundColor.MINT:
      bgClassName = 'u-bgColorMintLight10'
      break
    case BackgroundColor.MIDNIGHT_FACET:
      facetColor = 'midnight-blue'
      facet = hasFacet
        ? `url(${window.AMPCOMPATH}images/facets/banner/${facetColor}.svg)`
        : ''
      break
    case BackgroundColor.WHITE_FACET:
    case BackgroundColor.FADED_WHITE_FACET:
      facetColor = 'white'
      facet = hasFacet
        ? `url(${window.AMPCOMPATH}images/facets/banner/${facetColor}.svg)`
        : ''
      break
    case BackgroundColor.STONE:
      bgClassName = 'u-bgColorStone'
      break
    case BackgroundColor.PURPLE_TINT:
      bgClassName = 'u-bgColorPurpleLight10'
      break
    case BackgroundColor.LIME_TINT:
      bgClassName = 'u-bgColorLimeLight20'
      break
    case BackgroundColor.MINT_TINT:
      bgClassName = 'u-bgColorMintLight10'
      break
    case BackgroundColor.BERRY_TINT:
      bgClassName = 'u-bgColorBerryLight10'
      break
    case BackgroundColor.ORANGE_TINT:
      bgClassName = 'u-bgColorOrangeLight20'
      break
    default:
      bgClassName = 'u-bgColorMidnightBlue'
  }

  const getTextAlignmentClass = (textAlignment: string = 'center') => {
    let alignmentClass = ''
    if (textAlignment == 'left') {
      alignmentClass = 'u-textLeft'
    } else if (textAlignment == 'center') {
      alignmentClass = 'u-textCenter u-flexJustifyCenter'
    } else if (textAlignment == 'right') {
      alignmentClass = 'u-textRight u-flexJustifyEnd'
    }
    return alignmentClass
  }

  const linkClasses = ['u-marginHoriz8 u-sm-marginHoriz0 u-sm-block']
  if (bannerLinks.length == 1) {
    linkClasses.push('u-sm-marginBottom20')
  }
  if (buttonColor == 'white') {
    linkClasses.push('Button--reversed')
  }

  return (
    <Spacer spacing={componentSpacing}>
      <section
        className={[
          'OneColumnBanner',
          hasFacet ? 'u-bgSizeCover' : bgClassName,
          contentWidth === '8-column' && 'OneColumnBanner--eightColumn'
        ]
          .filter(Boolean)
          .join(' ')}
        style={{
          backgroundImage: backgroundImage ? `url(${backgroundImage})` : facet,
          backgroundPosition: '50%',
          backgroundRepeat: 'no-repeat',
          minHeight: '250px'
        }}
        data-testid={testId ? `${testId}-background` : null}
      >
        <div
          className={`u-flex u-flexCol u-sizeFill u-flexJustifyCenter u-paddingVert80 ${
            backgroundColor == BackgroundColor.FADED_WHITE_FACET
              ? 'OneColumnBanner-fadedFacet'
              : ''
          }`}
        >
          <div
            className={`u-sizeConstrained u-paddingVert4 ${contentWidth === '8-column' ? 'u-sm-sizeConstrainedFlush' : ''}`}
          >
            <div
              className={`OneColumnBanner-bannerContent Content ${
                contentWidth === '8-column'
                  ? 'u-lg-size8of12 u-lg-before2of12 u-lg-after2of12 u-size10of12 u-before1of12 u-after1of12'
                  : 'u-md-paddingHoriz24 u-sm-paddingHoriz8'
              }`}
            >
              {icon && (
                <div
                  className={`OneColumnBanner-icon u-marginBottom20 ${getTextAlignmentClass(
                    textAlignment
                  )}`}
                  data-testid={testId ? `${testId}-icon` : null}
                >
                  <BrandIcon
                    icon={icon}
                    color={iconColor}
                    fill={!!fill}
                    stroke={!!stroke}
                  />
                </div>
              )}
              {heading && (
                <div
                  className={`OneColumnBanner-heading Content u-marginBottom20 ${
                    textColor && textColor == 'white' ? 'Content--reversed' : ''
                  } ${getTextAlignmentClass(textAlignment)}`}
                  data-testid={testId ? `${testId}-heading` : null}
                  dangerouslySetInnerHTML={{ __html: xss(heading) }}
                />
              )}
              {bannerContent.length > 0 &&
                bannerContent.map((item, index) => (
                  <div
                    className={`OneColumnBanner-content Content u-marginBottom20 ${
                      textColor && textColor == 'white'
                        ? 'Content--reversed'
                        : ''
                    } ${getTextAlignmentClass(textAlignment)}`}
                    data-testid={testId ? `${testId}-content` : null}
                    key={`content-${index}`}
                    dangerouslySetInnerHTML={{ __html: xss(item) }}
                  />
                ))}
              {variant === OneColumnBannerVariant.ONE_COLUMN_BANNER_EMAIL &&
              advisorData?.email &&
              bannerLinks.length > 0 ? (
                <div
                  className={`OneColumnBanner-emailAdvisorButton u-marginTop48 ${getTextAlignmentClass(
                    textAlignment
                  )}`}
                >
                  <button
                    className={`Button Button--primary Button--small u-textNoWrap u-marginHoriz8 u-sm-marginHoriz0 u-sm-marginBottom20 ${
                      buttonColor == 'white' ? 'Button--reversed' : ''
                    }`}
                    onClick={handleOpenModal}
                    rel="noreferrer"
                    data-analytics={bannerLinks[0].analyticsName}
                  >
                    {bannerLinks[0].name}
                  </button>
                  <EmailAdvisorModal
                    isModalOpen={isModalOpen}
                    onClose={closeModal}
                    advisorInfo={{
                      name: advisorData?.name ?? '',
                      url: advisorData?.urls?.site
                    }}
                    payload={{
                      subject: emailSubject!,
                      greeting: emailGreeting!,
                      message: emailMessage() ?? '',
                      recipient: advisorData?.email,
                      template: EmailPayloadTemplate.CLIENT_INQUIRY,
                      overrideRecipient: overrideRecipient
                    }}
                    disclosureText={disclosureText}
                  />
                </div>
              ) : variant === OneColumnBannerVariant.ONE_COLUMN_BANNER_EMAIL &&
                advisorData?.phone &&
                bannerLinks.length > 0 ? (
                <div
                  className={`OneColumnBanner-callAdvisorLink u-marginTop48 ${getTextAlignmentClass(
                    textAlignment
                  )}`}
                >
                  <Link
                    key={`${bannerLinks[0].url || 'link-0'}`}
                    variant={LinkVariant.PRIMARY}
                    size={LinkSize.SMALL}
                    name={buttonTextPhone ?? 'Call Advisor'}
                    url={`tel:${advisorData.phone}`}
                    target={bannerLinks[0].target}
                    analyticsName={bannerLinks[0].analyticsName}
                    testId={testId ? `${testId}-button` : undefined}
                    className={`u-textNoWrap u-marginHoriz8 u-sm-marginHoriz0 u-sm-marginBottom20 ${
                      buttonColor == 'white' ? 'Button--reversed' : ''
                    }`}
                    ariaLabel={bannerLinks[0].ariaLabel}
                  />
                </div>
              ) : (
                <>
                  {bannerLinks.length > 0 && (
                    <div
                      className={`OneColumnBanner-links u-marginTop48 ${getTextAlignmentClass(
                        textAlignment
                      )}`}
                    >
                      {bannerLinks.map((link, index) => (
                        <Link
                          key={`${link.url || 'link'}-${index}`}
                          variant={
                            index == 0
                              ? (link.variant ?? LinkVariant.PRIMARY)
                              : LinkVariant.TERTIARY
                          }
                          size={LinkSize.SMALL}
                          name={link.name}
                          url={link.url}
                          target={link.target}
                          analyticsName={link.analyticsName}
                          testId={testId ? `${testId}-button` : undefined}
                          className={linkClasses.join(' ')}
                          ariaLabel={link.ariaLabel}
                        />
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </section>
    </Spacer>
  )
}
